// AddInstructorModal.js - Modal for adding a new instructor resource
import React from 'react';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	Select,
	SelectItem,
	Checkbox,
	addToast
} from '@heroui/react';
import { GraduationCap } from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';
import LocationsApi from '../../api/locationsApi.js';

function AddInstructorModal({
	isOpen,
	onClose,
	payStatuses = [],
	resources = [],
	resourcesTypes = [],
	onSuccess
}) {
	const [saving, setSaving] = React.useState(false);
	const [formData, setFormData] = React.useState({
		instructorResourceId: '',
		locationId: '',
		payStatus: '',
		rate: '',
		taxable: false
	});
	const [formErrors, setFormErrors] = React.useState({});

	// Locations from Symfony API
	const [apiLocations, setApiLocations] = React.useState([]);
	const [locationsLoading, setLocationsLoading] = React.useState(false);

	// Get instructor type ID from resourcesTypes
	const instructorTypeId = React.useMemo(() => {
		const instructorType = resourcesTypes.find(t => {
			const name = (t.name || '').toLowerCase();
			const unique = (t.unique || t.systemUnique || '').toLowerCase();
			return name === 'instructor' || name === 'instructors' || unique === 'instructor';
		});
		return instructorType?.id || null;
	}, [resourcesTypes]);

	// Filter resources to only show instructor resources
	const instructorResources = React.useMemo(() => {
		if (!instructorTypeId) return [];

		return resources.filter(resource => {
			// Support both nested (Symfony API) and flat (SSR) formats
			const typeId = resource.type?.id || resource.typeID;
			return String(typeId) === String(instructorTypeId);
		});
	}, [resources, instructorTypeId]);

	// Get unique instructors (by userID) for dropdown - each instructor may have multiple location entries
	const uniqueInstructors = React.useMemo(() => {
		const seen = new Map();
		instructorResources.forEach(resource => {
			// Support both nested (Symfony API) and flat (SSR) formats
			const userId = resource.user?.id || resource.userID || resource.userId;
			const key = userId || resource.id;
			if (!seen.has(key)) {
				seen.set(key, resource);
			}
		});
		return Array.from(seen.values());
	}, [instructorResources]);

	// Get locations where selected instructor is already assigned
	const assignedLocationIds = React.useMemo(() => {
		if (!formData.instructorResourceId) return new Set();

		// Find selected instructor's userId
		const selectedInstructor = uniqueInstructors.find(
			r => String(r.id) === String(formData.instructorResourceId)
		);
		if (!selectedInstructor) return new Set();

		const selectedUserId = selectedInstructor.user?.id || selectedInstructor.userID || selectedInstructor.userId;

		// Find all instructor resources for this user and collect their location IDs
		const locationIds = new Set();
		instructorResources.forEach(resource => {
			const userId = resource.user?.id || resource.userID || resource.userId;
			if (String(userId) === String(selectedUserId)) {
				const locationId = resource.location?.id || resource.locationID;
				if (locationId) {
					locationIds.add(String(locationId));
				}
			}
		});
		return locationIds;
	}, [formData.instructorResourceId, uniqueInstructors, instructorResources]);

	// Filter locations to exclude where instructor is already assigned
	const availableLocations = React.useMemo(() => {
		return apiLocations.filter(location => !assignedLocationIds.has(String(location.id)));
	}, [apiLocations, assignedLocationIds]);

	// Fetch locations from Symfony API when modal opens
	React.useEffect(() => {
		if (isOpen) {
			// Reset form
			setFormData({
				instructorResourceId: '',
				locationId: '',
				payStatus: '',
				rate: '',
				taxable: false
			});
			setFormErrors({});

			// Fetch locations from API
			const fetchLocations = async () => {
				setLocationsLoading(true);
				try {
					const response = await LocationsApi.getLocations();
					if (response.data?.success && response.data?.data) {
						setApiLocations(response.data.data);
					} else if (Array.isArray(response.data)) {
						setApiLocations(response.data);
					}
				} catch (error) {
					console.error('Error fetching locations:', error);
					setApiLocations([]);
				} finally {
					setLocationsLoading(false);
				}
			};
			fetchLocations();
		}
	}, [isOpen]);

	// Handle form input changes
	const handleInputChange = (field, value) => {
		setFormData(prev => {
			const newData = { ...prev, [field]: value };
			// Reset locationId when instructor changes (selected location may no longer be available)
			if (field === 'instructorResourceId') {
				newData.locationId = '';
			}
			return newData;
		});
		if (formErrors[field]) {
			setFormErrors(prev => ({ ...prev, [field]: null }));
		}
	};

	// Form validation
	const validateForm = () => {
		const errors = {};
		if (!formData.instructorResourceId) {
			errors.instructorResourceId = 'Instructor is required';
		}
		if (!formData.locationId) {
			errors.locationId = 'Location is required';
		}
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	// Handle form submission
	const handleSubmit = async () => {
		if (!validateForm()) return;

		if (!instructorTypeId) {
			addToast({
				title: 'Error',
				description: 'Instructor resource type not found',
				color: 'danger'
			});
			return;
		}

		setSaving(true);

		try {
			// Get selected instructor resource
			const selectedResource = uniqueInstructors.find(
				r => String(r.id) === String(formData.instructorResourceId)
			);

			if (!selectedResource) {
				throw new Error('Selected instructor not found');
			}

			// Support both nested (Symfony API) and flat (SSR) formats
			const userId = selectedResource.user?.id || selectedResource.userID || selectedResource.userId;
			const instructorName = selectedResource.name ||
				(selectedResource.user ? `${selectedResource.user.name || ''} ${selectedResource.user.surname || ''}`.trim() : 'Instructor');

			// Build resource data for API - duplicate instructor with new location
			const resourceData = {
				typeId: parseInt(instructorTypeId, 10),
				userId: parseInt(userId, 10),
				locationId: parseInt(formData.locationId, 10),
				name: instructorName,
				rate: formData.rate || null,
				payStatus: formData.payStatus || null,
				taxable: formData.taxable
			};

			const response = await ResourcesApi.addResource(resourceData);

			addToast({
				title: 'Success',
				description: `Instructor "${instructorName}" added successfully`,
				color: 'success'
			});

			onClose();
			if (onSuccess) {
				onSuccess(response.data);
			}
		} catch (error) {
			console.error('Error adding instructor:', error);
			const errorMessage =
				error.response?.data?.message ||
				error.response?.data?.error ||
				error.message ||
				'Failed to add instructor';
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		} finally {
			setSaving(false);
		}
	};

	// Get instructor display name from resource
	const getInstructorName = resource => {
		// Support both nested (Symfony API) and flat (SSR) formats
		if (resource.name) return resource.name;
		if (resource.user) {
			const fullName = `${resource.user.name || ''} ${resource.user.surname || ''}`.trim();
			if (fullName) return fullName;
		}
		return `Instructor ${resource.id}`;
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
			<ModalContent>
				<ModalHeader className="flex items-center gap-2">
					<GraduationCap className="w-5 h-5" />
					Add Instructor
				</ModalHeader>
				<ModalBody>
					<div className="space-y-4">
						{/* Instructor Select */}
						<Select
							label="Instructor"
							placeholder="Select instructor"
							variant="flat"
							selectedKeys={formData.instructorResourceId ? [formData.instructorResourceId] : []}
							onSelectionChange={keys => handleInputChange('instructorResourceId', Array.from(keys)[0])}
							isInvalid={!!formErrors.instructorResourceId}
							errorMessage={formErrors.instructorResourceId}
							isRequired
						>
							{uniqueInstructors.map(instructor => (
								<SelectItem key={String(instructor.id)}>
									{getInstructorName(instructor)}
								</SelectItem>
							))}
						</Select>

						{uniqueInstructors.length === 0 && (
							<p className="text-sm text-warning">
								No instructors available. Add an instructor resource first.
							</p>
						)}

						{/* Location Select - fetched from Symfony API, filtered to exclude already assigned */}
						<Select
							label="Location"
							placeholder={locationsLoading ? 'Loading locations...' : 'Select location'}
							variant="flat"
							selectedKeys={formData.locationId ? [formData.locationId] : []}
							onSelectionChange={keys => handleInputChange('locationId', Array.from(keys)[0])}
							isInvalid={!!formErrors.locationId}
							errorMessage={formErrors.locationId}
							isRequired
							isLoading={locationsLoading}
							isDisabled={locationsLoading || !formData.instructorResourceId}
						>
							{availableLocations.map(location => (
								<SelectItem key={String(location.id)}>
									{location.city ? `${location.name}, ${location.city}` : location.name}
								</SelectItem>
							))}
						</Select>

						{formData.instructorResourceId && availableLocations.length === 0 && !locationsLoading && (
							<p className="text-sm text-warning">
								This instructor is already assigned to all available locations.
							</p>
						)}

						{/* Charge Type Select */}
						<Select
							label="Charge Type"
							placeholder="Select charge type"
							variant="flat"
							selectedKeys={formData.payStatus ? [formData.payStatus] : ['none']}
							onSelectionChange={keys => {
								const value = Array.from(keys)[0];
								const newValue = value === 'none' ? '' : value;
								handleInputChange('payStatus', newValue);
								// Clear rate if no charge type selected
								if (!newValue) {
									handleInputChange('rate', '');
								}
							}}
						>
							<SelectItem key="none">Not Applicable</SelectItem>
							{payStatuses.map(status => (
								<SelectItem key={status.value}>{status.name}</SelectItem>
							))}
						</Select>

						{/* Rate Input */}
						<Input
							label="Rate"
							placeholder="Enter rate"
							variant="flat"
							type="number"
							step="0.01"
							min="0"
							startContent="$"
							value={formData.rate}
							onValueChange={value => handleInputChange('rate', value)}
							isDisabled={!formData.payStatus}
						/>

						{/* Tax Active Checkbox */}
						<Checkbox
							isSelected={formData.taxable}
							onValueChange={value => handleInputChange('taxable', value)}
						>
							Tax active
						</Checkbox>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button color="danger" variant="light" onPress={onClose}>
						Cancel
					</Button>
					<Button
						color="primary"
						onPress={handleSubmit}
						isLoading={saving}
						isDisabled={uniqueInstructors.length === 0}
					>
						Add Instructor
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

export default AddInstructorModal;
